import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { AuthValidationComposite } from "../AuthValidationComposite";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Flex,
  Modal
} from "@theorgchartdev/storybook";
import { ConnectorInputComponent } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSetupInputComposite";
import { DataSourceButton, GetSourceLabel } from "../DataSourceButton";
import { ImportTypes } from "@/Utils/Constants";
import {
  DATA_SOURCES_TEST_PREFIX,
  kConnectorDataKeys,
  kConnectorLegacyDataKeys,
  kConnectorPhotoOptionalKeys,
  kKeyToLabelAndDefaultText,
  kPasswordMaskingKeys,
  SetLastValidationTime
} from "../Utils";
import { AdditionalPhotoCustomContent } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/AdditionalPhotoConfig/AdditionalPhotoCustomContent";
import { appstr } from "@coreapp/utils/appstr";
import { appcstr } from "@coreapp/utils/appcstr";

const GetRequiredKeys = (source, credentials = {}) => {
  if (source === "Workday" && credentials.isOAuth) {
    return [...kConnectorDataKeys["WorkdayAPI"], "workdayhc"];
  }
  return [...(kConnectorDataKeys[source] ?? [])];
};

const GetOptionalKeys = source => {
  let optionalKeys = kConnectorPhotoOptionalKeys[source] ?? [];
  return [...optionalKeys];
};

const GetAllKeys = (source, credentials = {}) => {
  return [...GetRequiredKeys(source, credentials), ...GetOptionalKeys(source)];
};

const AreCredsFilled = (source, credentials = {}) => {
  var requiredKeys = GetRequiredKeys(source, credentials);

  return (
    source &&
    !requiredKeys.some(key => {
      const legacyValue = GetLegacyValue(source, key, credentials);
      return !credentials[key] && !legacyValue;
    })
  );
};

const GetInitialState = (source, credentials = {}) => {
  const keys = GetAllKeys(source, credentials);
  return [
    {
      key: "photoSource",
      value: source
    },
    ...keys.map(key => ({
      key,
      value: ""
    }))
  ];
};

const GetLegacyValue = (source, field, credentials = {}) => {
  const legacyKeys = kConnectorLegacyDataKeys[source]?.[field];
  if (!legacyKeys) {
    return;
  }

  const existingLegacyKey = legacyKeys.find(key => new Boolean(credentials[key]));
  if (!existingLegacyKey) {
    return;
  }

  return credentials[existingLegacyKey];
};

// Implements "GenerateTextEditParamsArray" behavior
const GenerateInputProps =
  (source, credentials = {}, handleChange) =>
  ({ key, value }) => {
    const legacyValue = GetLegacyValue(source, key, credentials);

    return {
      size: "medium",
      label: appcstr(kKeyToLabelAndDefaultText[key][0]),
      placeholder: appcstr(kKeyToLabelAndDefaultText[key][1]),
      onChange: handleChange(key),
      passwordMask: kPasswordMaskingKeys.includes(key?.toLowerCase()),
      value: (value || credentials[key] || legacyValue) ?? ""
    };
  };

export const AdditionalPhotoConfigModal = props => {
  const credentials = props.chartConfig?.import?.photo ?? {};
  const source = credentials.photoSource ?? null;

  const handleSave = () => {
    props.onSave();
    props.onClose();
  };

  const handleReset = () => {
    props.setChartConfig(prev => {
      SetLastValidationTime(prev, "photo", null);

      return {
        ...prev,
        import: {
          ...prev.import,
          photo: {
            photoSource: prev.import.photo.photoSource
          }
        }
      };
    });
  };

  const handleChange = key => e => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    props.setChartConfig(prev => {
      SetLastValidationTime(prev, "photo", null);

      return {
        ...prev,
        import: {
          ...prev.import,
          photo: {
            ...prev.import?.photo,
            [key]: value
          }
        }
      };
    });
  };

  const handleSelectChange = key => option => {
    props.setChartConfig(prev => {
      SetLastValidationTime(prev, "photo", null);

      return {
        ...prev,
        import: {
          ...prev.import,
          photo: {
            ...prev.import?.photo,
            [key]: option.value
          }
        }
      };
    });
  };

  const fieldState = GetInitialState(source, credentials).filter(
    ({ key }) => key !== "photoSource"
  );

  const allInputProps = fieldState.map(GenerateInputProps(source, credentials, handleChange));

  const isValidationEnabled = AreCredsFilled(source, credentials);

  let headerStr = `${appstr("Configure")} ${appstr("Photos").toLowerCase()}`;
  if (source) {
    headerStr = `${appstr("Configure")} ${GetSourceLabel(source, ImportTypes.photo)} ${appstr(
      "Photos"
    ).toLowerCase()}`;
  }

  return (
    <CentralizedModalContainer show={props.show}>
      <Modal
        show={props.show}
        id="additional-photo-config-modal"
        minWidth="medium"
        maxWidth="large"
        fullWidth={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={props.onClose}
          data-testid={`${DATA_SOURCES_TEST_PREFIX}-photo-config-modal-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            {source ? (
              <DataSourceButton
                chartConfig={props.chartConfig}
                dataSource={source}
                type={ImportTypes.photo}
                showLabel={false}
                imageOnly
                imageWidth={32}
                imageHeight={32}
                buttonPadding={1}
                buttonWidth={32}
              />
            ) : (
              <Icon size={"medium"} name={"gear"} />
            )}
            {headerStr}
          </span>
        </DialogTitle>
        <DialogContent>
          <Flex direction="column" gap="8px" padding="6px 0 0 0">
            {allInputProps.map(inputProps => {
              return (
                <ConnectorInputComponent
                  dataTestId={`${DATA_SOURCES_TEST_PREFIX}-photo-connector-${source}-${inputProps.label}`}
                  key={inputProps.label}
                  {...inputProps}
                />
              );
            })}
            <AdditionalPhotoCustomContent
              source={source}
              creds={credentials}
              onChange={handleChange}
              onSelectChange={handleSelectChange}
            />
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <Flex width={"100%"} justifyContent="space-between" padding={"4px"}>
            <AuthValidationComposite
              disabled={!isValidationEnabled}
              chartConfig={props.chartConfig}
              credentials={credentials}
              type={ImportTypes.photo}
            />
            <Flex gap="4px">
              <DialogActions.Secondary
                dataTestId={`${DATA_SOURCES_TEST_PREFIX}-photo-config-reset-button`}
                size={"large"}
                onClick={handleReset}
              >
                {appstr("Reset")}
              </DialogActions.Secondary>
              <DialogActions.Primary
                dataTestId={`${DATA_SOURCES_TEST_PREFIX}-photo-config-save-button`}
                size={"large"}
                onClick={handleSave}
              >
                {appstr("Save")}
              </DialogActions.Primary>
            </Flex>
          </Flex>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
